import { useCallback, useMemo } from "react";

import { type AttributionFilter, type CurrencyCode, type ExtendedMetric } from "@doitintl/cmp-models";
import { Link, Tooltip, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";

import { analyticsAlertText, globalText } from "../../../../assets/texts";
import { AlertsTxt, filterDialogText } from "../../../../assets/texts/CloudAnalytics";
import { CustomCard } from "../../../../Components/CustomCard/CustomCard";
import { useCloudAnalyticsTransforms } from "../../../../Components/hooks/cloudAnalytics/useCloudAnalyticsTransforms";
import {
  type AttributionMetadataOption,
  type AttributionWRef,
  type MetadataOption,
  type MetricWSnap,
} from "../../../../types";
import { assetTypeName } from "../../../../utils/common";
import { AnalyticsAlertCondition } from "../AnalyticsAlertCondition";
import { getScopeDescription } from "../shared";

type AnalyticsAlertsInfoPanelProps = {
  alertName: string;
  alertValue: number | "";
  calculatedMetric: MetricWSnap | null;
  condition: string;
  currency: CurrencyCode;
  currentStep: number;
  dimensionOptions: MetadataOption[];
  extendedMetricOptions: ExtendedMetric[];
  filter: AttributionFilter | null;
  metric: number;
  operator: string;
  scope: AttributionWRef[];
  evaluateByDimension: AttributionMetadataOption | null;
  selectedExtendedMetric: string | null;
  setOverrideStep: (step: number) => void;
  timeInterval: string;
};

export const AnalyticsAlertsInfoPanel = ({
  alertName,
  alertValue,
  calculatedMetric,
  condition,
  currency,
  currentStep,
  dimensionOptions,
  extendedMetricOptions,
  filter,
  metric,
  operator,
  scope,
  evaluateByDimension,
  selectedExtendedMetric,
  setOverrideStep,
  timeInterval,
}: AnalyticsAlertsInfoPanelProps) => {
  const transforms = useCloudAnalyticsTransforms();

  const getDimensionLabelFromId = useCallback(
    (id: string) => {
      const dimension = dimensionOptions.find((d) => d.id === id);
      return dimension?.data?.label ?? id;
    },
    [dimensionOptions]
  );

  const scopeTooltips = useMemo(
    () =>
      scope.map((s, index) => (
        <Tooltip
          title={
            <>
              {s.data.filters?.map((f) => (
                <Typography variant="body2" key={f.id}>
                  <b>{getDimensionLabelFromId(f.id)}</b> {f.values ? "is " : "matches regexp "}
                  <b>{f.values?.map((value) => assetTypeName(value)).join(", ") ?? `"${f.regexp}"`}</b>
                </Typography>
              ))}
            </>
          }
          placement="top"
          arrow
          key={s.ref.id}
        >
          <Typography
            variant="body2"
            component="span"
            sx={{
              display: "inline",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                display: "inline",
                textDecorationLine: "underline",
                textDecorationStyle: "dotted",
              }}
            >
              {s.data.name}
            </Typography>
            {index === scope.length - 1 ? "" : ","}{" "}
          </Typography>
        </Tooltip>
      )),
    [getDimensionLabelFromId, scope]
  );

  const dimensionsTooltip = useMemo(() => {
    const { label, values, desc } = getScopeDescription(filter ? [filter] : undefined, dimensionOptions, transforms);
    return (
      <Tooltip
        title={
          <Typography variant="body2">
            <b>{label}</b> {desc}
            <b>{values}</b>
          </Typography>
        }
        placement="top"
        arrow
      >
        <Typography
          variant="body2"
          sx={{
            display: "inline",
            textDecorationLine: "underline",
            textDecorationStyle: "dotted",
          }}
        >
          {label} (
          {filter?.regexp
            ? filterDialogText.USING_REGEX_LABEL
            : `${filter?.values?.length} ${AlertsTxt.VALUES_SELECTED}`}
          )
        </Typography>
      </Tooltip>
    );
  }, [filter, dimensionOptions, transforms]);

  const [conditionText] = AnalyticsAlertCondition({
    condition,
    currency,
    calculatedMetric,
    selectedExtendedMetric,
    extendedMetricOptions,
    metric,
    operator,
    timeInterval,
    alertValue,
    evaluateByDimension,
  });

  return (
    <Grid size={12}>
      <CustomCard style={{ mb: "2.5rem" }}>
        <Grid container size={12}>
          <Grid container sx={{ variant: "outlined" }} size={8}>
            <Typography variant="body2" sx={{ alignSelf: "right" }}>
              <b>{globalText.NAME}:</b> {alertName}
            </Typography>
          </Grid>
          <Grid
            container
            sx={{
              justifyContent: "flex-end",
            }}
            size={4}
          >
            <Link
              color="inherit"
              variant="body2"
              underline="always"
              href="#"
              onClick={() => {
                setOverrideStep(0);
              }}
            >
              {analyticsAlertText.EDIT_ALERT_SCOPE}
            </Link>
          </Grid>
          <Grid
            sx={{
              display: "inline-flex",
              variant: "outlined",
            }}
            size={9.3}
          >
            <Typography variant="body2" sx={{ alignSelf: "right", mt: 0.2 }}>
              <b>{analyticsAlertText.STEPPER.SCOPE}:</b>
            </Typography>
            &nbsp;
            <Typography component="span">{scopeTooltips.length > 0 ? scopeTooltips : dimensionsTooltip}</Typography>
          </Grid>
          {currentStep === 2 && (
            <>
              <Grid
                sx={{
                  display: "inline-flex",
                  variant: "outlined",
                }}
                size={9.3}
              >
                <Typography variant="body2" sx={{ alignSelf: "right" }}>
                  <b>{analyticsAlertText.STEPPER.CONDITION}:</b>
                </Typography>
                &nbsp;
                {conditionText}
              </Grid>
              <Grid
                container
                sx={{
                  justifyContent: "flex-end",
                }}
                size={2.7}
              >
                <Link
                  color="inherit"
                  variant="body2"
                  underline="always"
                  href="#"
                  onClick={() => {
                    setOverrideStep(1);
                  }}
                >
                  {analyticsAlertText.EDIT_CONDITIONS}
                </Link>
              </Grid>
            </>
          )}
        </Grid>
      </CustomCard>
    </Grid>
  );
};
